import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Calculator,
  CheckCircle2,
  Trash2,
  ArrowLeft,
  Calendar,
  DollarSign,
  Receipt,
  TrendingDown,
  FileText,
  Printer,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";
import SignaturePad, { SignatureImage, printElement } from "../components/SignaturePad";
import PayslipPrint from "../components/PayslipPrint";
import PayrollSimulator from "../components/PayrollSimulator";

const statusMap = {
  draft:      { label: "Borrador",   cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  calculated: { label: "Calculado",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved:   { label: "Aprobado",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paid:       { label: "Pagado",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

const MONTHS = [
  "", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const INCIDENT_TYPE_OPTIONS = [
  { value: "incapacidad_ccss", label: "Incapacidad CCSS" },
  { value: "licencia_maternidad", label: "Licencia de Maternidad" },
  { value: "incapacidad_ins", label: "Incapacidad INS" },
  { value: "vacaciones", label: "Vacaciones" },
  { value: "permiso_con_goce", label: "Permiso con goce" },
  { value: "permiso_sin_goce", label: "Permiso sin goce" },
  { value: "ausencia_justificada", label: "Ausencia justificada" },
  { value: "ausencia_injustificada", label: "Ausencia injustificada (sin pago)" },
];

const money = (n) =>
  `₡ ${Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = new Date();

const emptyForm = {
  year: today.getFullYear(),
  month: today.getMonth() + 1,
  half: today.getDate() <= 15 ? 1 : 2,
  branch_id: "all",
  notes: "",
};

export default function Payroll() {
  const [periods, setPeriods] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [entries, setEntries] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [entryDetail, setEntryDetail] = useState(null);
  const [earned, setEarned] = useState(null); // {as_of_date, period_started, map}
  const [dayEdit, setDayEdit] = useState(null); // {date, day_name} being edited
  const [dayForm, setDayForm] = useState({});
  const [savingDay, setSavingDay] = useState(false);
  const [dedForm, setDedForm] = useState({ open: false, concept: "", amount: "", notes: "" });
  const [savingDed, setSavingDed] = useState(false);
  const [addForm, setAddForm] = useState({ open: false, concept: "", amount: "", notes: "", subject_to_ccss: false });
  const [savingAdd, setSavingAdd] = useState(false);
  const [pendingSignature, setPendingSignature] = useState(null);
  const [pendingSignerId, setPendingSignerId] = useState("");
  const [signing, setSigning] = useState(false);
  const [defaultCompany, setDefaultCompany] = useState(null);
  const receiptRef = useRef(null);
  const payslipRef = useRef(null);

  // Fallback: primary company info for receipts whose entries were computed
  // before the company-info enrichment landed.
  useEffect(() => {
    api.get("/companies")
      .then(({ data }) => {
        const primary = (data || []).find((c) => c.is_primary) || (data || [])[0] || null;
        setDefaultCompany(primary);
      })
      .catch(() => {});
  }, []);

  const fetchPeriods = useCallback(async () => {
    try {
      const [pRes, bRes] = await Promise.all([
        api.get("/payroll/periods"),
        api.get("/branches"),
      ]);
      setPeriods(pRes.data);
      setBranches(bRes.data);
    } catch {
      toast.error("Error al cargar periodos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  // "Ganado hasta ayer" — solo para periodos abiertos (no aprobados).
  useEffect(() => {
    const pid = selectedPeriod?.id;
    if (!pid || selectedPeriod?.status === "approved") {
      setEarned(null);
      return;
    }
    let cancelled = false;
    api.get(`/payroll/periods/${pid}/earned-to-date`)
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data.items || []).forEach((i) => { map[i.employee_id] = i; });
        setEarned({ ...data, map });
      })
      .catch(() => { if (!cancelled) setEarned(null); });
    return () => { cancelled = true; };
  }, [selectedPeriod?.id, selectedPeriod?.status, selectedPeriod?.calculated_at]);

  const openDetail = async (pid) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/payroll/periods/${pid}`);
      setSelectedPeriod(data);
      setEntries(data.entries || []);
    } catch {
      toast.error("Error al cargar detalle");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        year: Number(form.year),
        month: Number(form.month),
        half: Number(form.half),
        branch_id: form.branch_id === "all" ? null : form.branch_id,
        notes: form.notes,
      };
      const { data } = await api.post("/payroll/periods", payload);
      toast.success("Periodo creado");
      setDialogOpen(false);
      setForm(emptyForm);
      fetchPeriods();
      openDetail(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear periodo");
    }
  };

  const handleCalculate = async (pid, opts = {}) => {
    const toastId = toast.loading("Calculando nómina...");
    try {
      const url = opts.force
        ? `/payroll/periods/${pid}/calculate?force=true`
        : `/payroll/periods/${pid}/calculate`;
      const { data } = await api.post(url);
      const projMsg = data.has_projections
        ? ` · ⏱ ${data.entries.filter((e) => e.has_projections).length} con proyección`
        : "";
      toast.success(`Cálculo completado: ${data.employee_count} empleados${projMsg}`, { id: toastId });
      setSelectedPeriod(data);
      setEntries(data.entries || []);
      fetchPeriods();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al calcular";
      if (detail.includes("No se puede calcular antes del corte")) {
        toast.dismiss(toastId);
        if (window.confirm(`${detail}\n\n¿Calcular igualmente como simulación?`)) {
          return handleCalculate(pid, { force: true });
        }
        return;
      }
      toast.error(detail, { id: toastId });
    }
  };

  // Recalcula SOLO el comprobante del empleado editado (rápido; evita recalcular
  // toda la planilla y que el guardado tarde o falle por timeout del proxy).
  const recalcEntry = async (empId) => {
    const pid = selectedPeriod.id;
    const { data } = await api.post(`/payroll/periods/${pid}/entries/${empId}/recalculate`);
    if (data.period) setSelectedPeriod(data.period);
    if (data.entry) {
      setEntries((prev) => prev.map((e) => (e.employee_id === empId ? data.entry : e)));
    }
    return data.entry;
  };

  // Recalcular SOLO este comprobante desde sus marcas existentes (no edita el día,
  // no toca a los demás trabajadores). Seguro para días con jornada partida.
  const [recalcingEntry, setRecalcingEntry] = useState(false);
  const recalcThisEntry = async () => {
    if (!entryDetail || !selectedPeriod) return;
    setRecalcingEntry(true);
    try {
      const updated = await recalcEntry(entryDetail.employee_id);
      if (updated) setEntryDetail(updated);
      toast.success("Comprobante recalculado con las marcas actuales");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo recalcular el comprobante");
    } finally {
      setRecalcingEntry(false);
    }
  };

  const openDayEdit = (day) => {
    setDayEdit(day);
    const marked = (day.status || "").toLowerCase();
    setDayForm({
      mode: day.incident_type ? "incident" : "attendance",
      check_in_time: day.check_in ? day.check_in.slice(11, 16) : "",
      check_out_time: day.check_out ? day.check_out.slice(11, 16) : "",
      unpaid_break_out_time: day.unpaid_break_out ? day.unpaid_break_out.slice(11, 16) : "",
      unpaid_break_in_time: day.unpaid_break_in ? day.unpaid_break_in.slice(11, 16) : "",
      authorize_outside_as_extra: !!day.authorized_outside_extra,
      incident_type: day.incident_type || "incapacidad_ccss",
      incident_id: day.incident_id || null,
      folio: "",
      notes: "",
    });
    void marked;
  };
  const closeDayEdit = () => {
    setDayEdit(null);
    setDayForm({});
  };
  const toggleHolidaySingle = async (single) => {
    if (!dayEdit || !entryDetail || !selectedPeriod) return;
    const empId = entryDetail.employee_id;
    const d = dayEdit.date;
    const pid = selectedPeriod.id;
    setSavingDay(true);
    try {
      await api.put(`/payroll/periods/${pid}/holiday-single`, {
        employee_id: empId,
        date: d,
        single,
      });
      const updated = await recalcEntry(empId);
      if (updated) {
        setEntryDetail(updated);
        const newDay = (updated.daily_breakdown || []).find((x) => x.date === d);
        if (newDay) setDayEdit(newDay);
      }
      toast.success(single ? "Feriado marcado como sencillo" : "Feriado vuelto a pago doble");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar el feriado");
    } finally {
      setSavingDay(false);
    }
  };
  const saveDayEdit = async () => {
    if (!dayEdit || !entryDetail || !selectedPeriod) return;
    const empId = entryDetail.employee_id;
    const d = dayEdit.date;
    const pid = selectedPeriod.id;
    setSavingDay(true);
    try {
      // Resolver incidencia existente de UN solo día en esa fecha (robusto: no
      // depende de que el desglose traiga el id → evita crear duplicados).
      let existingIncidentId = dayForm.incident_id || null;
      if (!existingIncidentId) {
        try {
          const { data: existing } = await api.get("/incidents", {
            params: { employee_id: empId, active_on: d },
          });
          const sameDay = (existing || []).find((x) => x.start_date === d && x.end_date === d);
          if (sameDay) existingIncidentId = sameDay.id;
        } catch { /* sin bloquear */ }
      }
      if (dayForm.mode === "incident") {
        if (existingIncidentId) {
          // Ya existe una incidencia ese día → ACTUALIZAR (no crear otra).
          await api.patch(`/incidents/${existingIncidentId}`, {
            type: dayForm.incident_type,
            start_date: d,
            end_date: d,
          });
        } else {
          await api.post("/incidents", {
            employee_id: empId,
            type: dayForm.incident_type,
            start_date: d,
            end_date: d,
            folio: dayForm.folio || "",
            notes: dayForm.notes || "Registrado desde nómina",
          });
        }
      } else {
        // Modo asistencia: si el día tenía una incidencia, se elimina para que
        // las marcas tomen efecto (evita que la incidencia siga mandando).
        if (existingIncidentId) {
          await api.delete(`/incidents/${existingIncidentId}`);
        }
        const ci = dayForm.check_in_time ? `${d}T${dayForm.check_in_time}:00` : null;
        const co = dayForm.check_out_time ? `${d}T${dayForm.check_out_time}:00` : null;
        if (!ci || !co) {
          toast.error("Ingrese entrada y salida");
          setSavingDay(false);
          return;
        }
        const bothBreak = dayForm.unpaid_break_out_time && dayForm.unpaid_break_in_time;
        const bo = bothBreak ? `${d}T${dayForm.unpaid_break_out_time}:00` : "";
        const bi = bothBreak ? `${d}T${dayForm.unpaid_break_in_time}:00` : "";
        const { data: recs } = await api.get("/attendance/records", {
          params: { employee_id: empId, date: d },
        });
        if (recs && recs.length) {
          await api.patch(`/attendance/records/${recs[0].id}`, {
            first_check_in: ci,
            final_check_out: co,
            unpaid_break_out: bo,
            unpaid_break_in: bi,
            authorize_outside_as_extra: !!dayForm.authorize_outside_as_extra,
            attendance_status: "present",
            notes: "Editado desde nómina",
          });
        } else {
          await api.post("/attendance/records/manual", {
            employee_id: empId,
            date: d,
            first_check_in: ci,
            final_check_out: co,
            unpaid_break_out: bo || null,
            unpaid_break_in: bi || null,
            authorize_outside_as_extra: !!dayForm.authorize_outside_as_extra,
            attendance_status: "present",
            notes: "Registrado desde nómina",
          });
        }
      }
      // Recalcular SOLO este empleado (rápido; evita timeout recalculando toda la planilla)
      const updated = await recalcEntry(empId);
      if (updated) setEntryDetail(updated);
      toast.success("Día actualizado y nómina recalculada");
      closeDayEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el día");
    } finally {
      setSavingDay(false);
    }
  };

  const addDeduction = async () => {
    if (!entryDetail || !selectedPeriod) return;
    const concept = (dedForm.concept || "").trim();
    const amount = parseFloat(dedForm.amount);
    if (!concept) { toast.error("Ingrese el concepto"); return; }
    if (!amount || amount <= 0) { toast.error("Ingrese un monto válido"); return; }
    setSavingDed(true);
    try {
      await api.post(`/payroll/periods/${selectedPeriod.id}/deductions`, {
        employee_id: entryDetail.employee_id,
        concept,
        amount,
        notes: dedForm.notes || "",
      });
      const updated = await recalcEntry(entryDetail.employee_id);
      if (updated) setEntryDetail(updated);
      toast.success("Deducción agregada");
      setDedForm({ open: false, concept: "", amount: "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar la deducción");
    } finally {
      setSavingDed(false);
    }
  };

  const removeDeduction = async (dedId) => {
    if (!entryDetail || !selectedPeriod) return;
    try {
      await api.delete(`/payroll/deductions/${dedId}`);
      const updated = await recalcEntry(entryDetail.employee_id);
      if (updated) setEntryDetail(updated);
      toast.success("Deducción eliminada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const addAddition = async () => {
    if (!entryDetail || !selectedPeriod) return;
    const concept = (addForm.concept || "").trim();
    const amount = parseFloat(addForm.amount);
    if (!concept) { toast.error("Ingrese el concepto"); return; }
    if (!amount || amount <= 0) { toast.error("Ingrese un monto válido"); return; }
    setSavingAdd(true);
    try {
      await api.post(`/payroll/periods/${selectedPeriod.id}/additions`, {
        employee_id: entryDetail.employee_id,
        concept,
        amount,
        notes: addForm.notes || "",
        subject_to_ccss: !!addForm.subject_to_ccss,
      });
      const updated = await recalcEntry(entryDetail.employee_id);
      if (updated) setEntryDetail(updated);
      toast.success("Adicional agregado");
      setAddForm({ open: false, concept: "", amount: "", notes: "", subject_to_ccss: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar el adicional");
    } finally {
      setSavingAdd(false);
    }
  };

  const removeAddition = async (addId) => {
    if (!entryDetail || !selectedPeriod) return;
    try {
      await api.delete(`/payroll/additions/${addId}`);
      const updated = await recalcEntry(entryDetail.employee_id);
      if (updated) setEntryDetail(updated);
      toast.success("Adicional eliminado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const handleApprove = async (pid) => {
    if (!window.confirm("Aprobar este periodo de nómina? Esta acción lo cierra y no podrá recalcularse.")) return;
    try {
      await api.post(`/payroll/periods/${pid}/approve`, { approved_by: "RRHH" });
      toast.success("Periodo aprobado");
      openDetail(pid);
      fetchPeriods();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.reason === "missing_records") {
        const offs = detail.offenders || [];
        const shown = offs.slice(0, 8);
        const names = shown
          .map((o) => `${o.employee_name || o.employee_code || "Trabajador"} (${(o.dates || []).map(formatDate).join(", ")})`)
          .join("\n• ");
        const extra = offs.length > shown.length ? `\n• …y ${offs.length - shown.length} trabajador(es) más` : "";
        toast.error(detail.message, {
          duration: 12000,
          description: names ? `• ${names}${extra}` : undefined,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al aprobar");
      }
    }
  };

  const handleDelete = async (pid, status) => {
    const isApproved = status === "approved";
    const msg = isApproved
      ? "⚠️ Este periodo está APROBADO.\n\nAl eliminarlo se borrarán sus comprobantes y se revertirán los pagos de vales aplicados (vuelven al saldo) y los ajustes.\n\n¿Seguro que deseas eliminarlo? Esta acción no se puede deshacer."
      : "¿Eliminar este periodo de nómina?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/payroll/periods/${pid}`);
      toast.success("Periodo eliminado");
      if (selectedPeriod?.id === pid) setSelectedPeriod(null);
      fetchPeriods();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "Todas";

  // ──────────────────────────────────────────────────────────────────────
  // DETAIL VIEW
  // ──────────────────────────────────────────────────────────────────────
  if (selectedPeriod) {
    const p = selectedPeriod;
    const totalEmp = entries.length;
    const showEarned = p.status !== "approved" && earned?.period_started;
    return (
      <div data-testid="payroll-detail-page">
        <Button
          variant="ghost"
          onClick={() => { setSelectedPeriod(null); setEntries([]); }}
          className="mb-4 text-zinc-500 hover:text-zinc-900"
          data-testid="payroll-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver a periodos
        </Button>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
              Nómina {MONTHS[p.month]} {p.year} · Quincena {p.half}
            </h1>
            <p className="text-sm text-zinc-500 mt-1 font-mono">
              {formatDate(p.start_date)} → {formatDate(p.end_date)} · {branchName(p.branch_id)}
            </p>
            {p.payment_date && (
              <p className="text-xs text-zinc-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  <span className="text-zinc-400">Fecha de pago:</span>{" "}
                  <span className="font-semibold text-zinc-700">{formatDate(p.payment_date)}</span>
                  {p.payment_date_shifted && (
                    <span className="ml-1 text-amber-600">
                      (movida desde {formatDate(p.natural_payment_date)} por domingo/feriado)
                    </span>
                  )}
                </span>
                <span>
                  <span className="text-zinc-400">Corte:</span>{" "}
                  <span className="font-semibold text-zinc-700">{formatDate(p.cutoff_date)}</span>
                </span>
              </p>
            )}
          </div>
          <Badge variant="outline" className={`font-medium ${statusMap[p.status]?.cls}`}>
            {statusMap[p.status]?.label}
          </Badge>
        </div>

        {/* Projection banner */}
        {p.has_projections && p.status === "calculated" && (
          <div
            className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex gap-3 items-start"
            data-testid="payroll-projection-banner"
          >
            <div className="mt-0.5 w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
              <Calculator className="w-4 h-4 text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                Esta nómina incluye días proyectados
              </p>
              <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                {entries.filter((e) => e.has_projections).length} trabajador(es) tienen al menos un día
                marcado como <strong>"Proyectado · jornada del contrato"</strong>.
                Cuando ese día pase y se reciban las marcas reales, el sistema calculará automáticamente
                la diferencia y la aplicará como <strong>"Ajuste período anterior"</strong> en la próxima planilla.
                Si alguien ya avisó que no se presentará, registre una incidencia ahora para evitar el ajuste.
              </p>
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-emerald-600" strokeWidth={1.75} />
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Total Bruto</p>
              </div>
              <p className="text-xl font-bold text-zinc-950">{money(p.total_gross)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-red-600" strokeWidth={1.75} />
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Deducciones</p>
              </div>
              <p className="text-xl font-bold text-zinc-950">{money(p.total_deductions)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-4 h-4 text-zinc-900" strokeWidth={1.75} />
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Neto a pagar</p>
              </div>
              <p className="text-xl font-bold text-zinc-950">{money(p.total_net)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Empleados</p>
              <p className="text-xl font-bold text-zinc-950 mt-1">{totalEmp}</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mb-6">
          <TooltipProvider delayDuration={150}>
            {p.status !== "approved" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleCalculate(p.id)}
                    className="bg-zinc-900 text-white hover:bg-zinc-800"
                    data-testid="payroll-calculate-button"
                  >
                    <Calculator className="w-4 h-4 mr-2" /> {p.status === "draft" ? "Calcular nómina" : "Recalcular"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-zinc-900 text-white text-xs max-w-xs">
                  {p.status === "draft"
                    ? "Genera los comprobantes de todos los trabajadores del periodo a partir de sus marcas de asistencia, incidencias y días proyectados."
                    : "Vuelve a calcular los comprobantes con la información más reciente (marcas, incidencias, deducciones y días proyectados). Úsalo antes de aprobar."}
                </TooltipContent>
              </Tooltip>
            )}
            {p.status === "calculated" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleApprove(p.id)}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="payroll-approve-button"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Aprobar periodo
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-zinc-900 text-white text-xs max-w-xs">
                  Cierra el periodo de forma definitiva: aplica los vales y ajustes, habilita la firma e impresión de comprobantes y ya no se podrá recalcular ni editar.
                </TooltipContent>
              </Tooltip>
            )}
            {p.status !== "draft" && entries.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/payroll-cost/pdf?period_id=${p.id}`, "_blank")}
                    className="border-zinc-300"
                    data-testid="payroll-cost-report-button"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Reporte de costo (PDF)
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-zinc-900 text-white text-xs max-w-xs">
                  Descarga un PDF con el costo de la planilla de este periodo desglosado por sucursal (salario bruto, deducciones, neto y carga patronal CCSS).
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>

        {/* Entries table */}
        <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-100 hover:bg-transparent">
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Empleado</TableHead>
                  {showEarned && (
                    <TableHead
                      className="font-medium text-emerald-700 text-xs uppercase tracking-wide text-right"
                      data-testid="earned-to-date-header"
                    >
                      Ganado hasta {earned?.as_of_date ? formatDate(earned.as_of_date) : "ayer"}
                    </TableHead>
                  )}
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Ord h</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Extra h</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden md:table-cell">Feriado h</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden md:table-cell">Incid. paga</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden lg:table-cell">Incid. no</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Bruto</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden md:table-cell">CCSS</TableHead>
                  <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Neto</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow
                    key={e.id}
                    className="border-zinc-100 hover:bg-zinc-50/80 transition-colors cursor-pointer"
                    onClick={() => setEntryDetail(e)}
                    data-testid={`payroll-entry-row-${e.employee_id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="text-sm text-zinc-900 font-medium">{e.employee_name}</p>
                        <p className="text-[11px] text-zinc-400 font-mono">{e.employee_code}</p>
                      </div>
                    </TableCell>
                    {showEarned && (
                      <TableCell
                        className="text-right text-sm text-emerald-700 font-semibold font-mono"
                        data-testid={`earned-to-date-${e.employee_id}`}
                      >
                        {earned?.map?.[e.employee_id]
                          ? money(earned.map[e.employee_id].earned_net)
                          : <span className="text-zinc-300">—</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-right text-sm text-zinc-700 font-mono">{e.ordinary_hours}</TableCell>
                    <TableCell className="text-right text-sm text-zinc-700 font-mono">{e.overtime_hours}</TableCell>
                    <TableCell className="text-right text-sm text-zinc-700 font-mono hidden md:table-cell">{e.holiday_hours}</TableCell>
                    <TableCell className="text-right text-sm text-emerald-700 font-mono hidden md:table-cell">{e.incident_paid_days}</TableCell>
                    <TableCell className="text-right text-sm text-red-600 font-mono hidden lg:table-cell">{e.incident_unpaid_days}</TableCell>
                    <TableCell className="text-right text-sm text-zinc-900 font-semibold font-mono">{money(e.gross_pay)}</TableCell>
                    <TableCell className="text-right text-sm text-red-600 font-mono hidden md:table-cell">-{money(e.ccss_deduction)}</TableCell>
                    <TableCell className="text-right text-sm text-zinc-950 font-bold font-mono">{money(e.net_pay)}</TableCell>
                    <TableCell>
                      <FileText className="w-4 h-4 text-zinc-300" />
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && !detailLoading && (
                  <TableRow>
                    <TableCell colSpan={showEarned ? 11 : 10} className="text-center py-16 text-zinc-400">
                      <Calculator className="w-10 h-10 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Aún no se ha calculado el periodo</p>
                      <p className="text-xs mt-1">Presiona "Calcular nómina" para generar las entradas</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Entry receipt modal */}
        <Dialog open={!!entryDetail} onOpenChange={(o) => !o && setEntryDetail(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-zinc-950">Comprobante de pago</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {entryDetail?.employee_name} · {entryDetail?.employee_code}
              </DialogDescription>
              {selectedPeriod?.status !== "approved" && (
                <div className="pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={recalcThisEntry}
                    disabled={recalcingEntry}
                    className="h-8 text-xs"
                    data-testid="recalc-entry-button"
                  >
                    <Calculator className="w-3.5 h-3.5 mr-1.5" />
                    {recalcingEntry ? "Recalculando..." : "Recalcular este comprobante"}
                  </Button>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    Recalcula solo a este trabajador con sus marcas actuales. No edita el día ni afecta a los demás.
                  </p>
                </div>
              )}
            </DialogHeader>
            {entryDetail && (
              <div ref={receiptRef} className="p-5 bg-white text-zinc-950 text-[13px]" data-testid="payroll-receipt-modal">
                {/* Header */}
                <div className="flex items-start justify-between border-b-2 border-zinc-900 pb-3 mb-4">
                  <div>
                    <p className="text-base font-bold leading-tight">
                      {entryDetail.company_legal_name
                        || entryDetail.company_trade_name
                        || defaultCompany?.legal_name
                        || defaultCompany?.trade_name
                        || "—"}
                    </p>
                    <p className="text-[11px] text-zinc-500">
                      Cédula jurídica: {entryDetail.company_legal_id || defaultCompany?.legal_id || "—"}
                    </p>
                    {(entryDetail.company_address || defaultCompany?.address) && (
                      <p className="text-[10px] text-zinc-400 leading-tight">
                        {entryDetail.company_address || defaultCompany?.address}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-wider text-zinc-400">Comprobante de pago</p>
                    <p className="font-mono text-xs text-zinc-600">#{entryDetail.id?.slice(0, 8).toUpperCase()}</p>
                    <p className="text-[11px] text-zinc-500">{formatDate(p.start_date)} → {formatDate(p.end_date)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                  <div><span className="text-zinc-500">Trabajador:</span> <strong>{entryDetail.employee_name}</strong></div>
                  <div><span className="text-zinc-500">Ocupación:</span> <strong>{entryDetail.occupation || "—"}</strong></div>
                </div>

                {/* DETALLE DIARIO */}
                {(entryDetail.daily_breakdown || []).length > 0 && (
                  <div className="mb-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 border-b border-zinc-200 pb-1 mb-1">
                      Detalle por día
                    </p>
                    {(() => {
                      const sinReg = (entryDetail.daily_breakdown || []).filter((d) => d.status === "Sin registro");
                      if (sinReg.length === 0) return null;
                      return (
                        <div
                          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 mb-2"
                          data-testid="receipt-no-record-warning"
                        >
                          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                          <p className="text-[11px] text-red-700 leading-snug">
                            <strong>{sinReg.length} día(s) sin registro</strong> ({sinReg.map((d) => formatDate(d.date)).join(", ")}).
                            Estos días pagan ₡0 (entrada sin salida o ausencia). Revíselos y registre la salida o una incidencia
                            {p.status !== "approved" ? " tocando el día en la tabla" : ""} antes de aprobar la quincena.
                          </p>
                        </div>
                      );
                    })()}
                    <div className="overflow-x-auto -mx-1 px-1">
                      <table className="w-full text-[11px]" data-testid="receipt-daily-breakdown">
                        <thead>
                          <tr className="text-zinc-500 uppercase tracking-wide text-[10px] border-b border-zinc-100">
                            <th className="text-left py-1 pr-2 font-medium">Fecha</th>
                            <th className="text-left py-1 px-2 font-medium">Día</th>
                            <th className="text-left py-1 px-2 font-medium">Estado</th>
                            <th className="text-right py-1 px-2 font-medium">Ord.</th>
                            <th className="text-right py-1 px-2 font-medium">Extra</th>
                            <th className="text-right py-1 px-2 font-medium">Pago día</th>
                            <th className="text-right py-1 pl-2 font-medium">Recargos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entryDetail.daily_breakdown.map((d) => (
                            <tr
                              key={d.date}
                              className={`border-b border-zinc-50 ${
                                d.status === "Sin registro" ? "bg-red-50" :
                                d.is_projected ? "bg-amber-50/70" :
                                !d.is_business_day ? "bg-zinc-50/60" :
                                d.is_holiday ? "bg-amber-50/40" : ""
                              } ${p.status !== "approved" ? "cursor-pointer hover:bg-zinc-100" : ""}`}
                              onClick={p.status !== "approved" ? () => openDayEdit(d) : undefined}
                              title={p.status !== "approved" ? "Editar este día (registrar entrada/salida o incidencia)" : undefined}
                              data-testid={`receipt-day-${d.date}`}
                            >
                              <td className="py-1 pr-2 font-mono text-zinc-700">{formatDate(d.date)}</td>
                              <td className="py-1 px-2 text-zinc-600">{d.day_name}</td>
                              <td className={`py-1 px-2 ${d.status === "Sin registro" ? "text-red-700 font-semibold" : "text-zinc-700"}`}>
                                {d.is_projected && (
                                  <span className="inline-block mr-1 px-1 rounded bg-amber-200 text-amber-900 text-[9px] font-bold uppercase tracking-wide">
                                    Proy
                                  </span>
                                )}
                                {d.status === "Sin registro" && (
                                  <AlertTriangle className="inline-block w-3 h-3 mr-1 -mt-0.5 text-red-600" />
                                )}
                                {d.status}
                                {d.is_holiday && <span className="ml-1 text-[9px] text-amber-700 uppercase">FER</span>}
                              </td>
                              <td className="py-1 px-2 text-right font-mono text-zinc-700">{d.ordinary_hours ? d.ordinary_hours.toFixed(2) : "—"}</td>
                              <td className="py-1 px-2 text-right font-mono text-zinc-700">{d.overtime_hours ? d.overtime_hours.toFixed(2) : "—"}</td>
                              <td className="py-1 px-2 text-right font-mono">
                                {d.day_deduction > 0 ? (
                                  <span className="text-red-600">{money(d.day_pay)}</span>
                                ) : d.day_pay > 0 ? (
                                  money(d.day_pay)
                                ) : "—"}
                              </td>
                              <td className="py-1 pl-2 text-right font-mono text-emerald-700">
                                {d.day_extra > 0 ? `+${money(d.day_extra)}` : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1">
                      Fila gris = día no laborable · Fila ámbar = feriado · <strong className="text-amber-700">Fila ámbar fuerte + “Proy” = día proyectado (jornada del contrato; se reconciliará en la próxima nómina)</strong> · Pago día en rojo = pago parcial por incidencia.
                      {p.status !== "approved" && (
                        <span className="block text-zinc-500 mt-0.5">
                          <strong>Toca cualquier día</strong> para editar su registro o marcar una incidencia antes de aprobar la nómina.
                        </span>
                      )}
                    </p>

                    {/* Ajustes período anterior */}
                    {(entryDetail.adjustments_applied || []).length > 0 && (
                      <div className="mt-3 pt-2 border-t border-dashed border-zinc-300" data-testid="receipt-adjustments">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 mb-1">
                          Ajustes período anterior
                        </p>
                        <table className="w-full text-[11px]">
                          <tbody>
                            {entryDetail.adjustments_applied.map((a) => (
                              <tr key={a.id} className="border-b border-zinc-50">
                                <td className="py-1 pr-2 font-mono text-zinc-700">
                                  {a.target_date?.slice(8, 10)}/{a.target_date?.slice(5, 7)}
                                </td>
                                <td className="py-1 px-2 text-zinc-700">{a.reason}</td>
                                <td className="py-1 pl-2 text-right font-mono">
                                  <span className={a.delta_pay < 0 ? "text-red-600" : "text-emerald-700"}>
                                    {a.delta_pay >= 0 ? "+" : ""}{money(a.delta_pay)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-zinc-50">
                              <td colSpan={2} className="py-1 px-2 text-right font-semibold text-zinc-700">
                                Total ajustes
                              </td>
                              <td className="py-1 pl-2 text-right font-mono font-bold">
                                {entryDetail.adjustments_total >= 0 ? "+" : ""}{money(entryDetail.adjustments_total)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* INGRESOS */}
                <div className="mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 border-b border-zinc-200 pb-1 mb-1">Totales del periodo</p>
                  <div className="divide-y divide-zinc-100">
                    {entryDetail.payment_type === "monthly" && (
                      <Row label="Salario base (quincena)" value={money(entryDetail.quincena_base)} />
                    )}
                    {entryDetail.payment_type === "monthly" ? (
                      <>
                        <Row label={`Horas ordinarias (${entryDetail.ordinary_hours} h)`} value={money(entryDetail.ordinary_pay)} />
                        {entryDetail.incident_unpaid_deduction > 0 && (
                          <p className="text-[10px] text-zinc-400 py-1" data-testid="ordinary-incident-note">
                            Incluye rebaja por incidencias no pagadas ({(entryDetail.incident_unpaid_equivalent || 0).toFixed(2)} d equiv.): <strong className="text-zinc-500">-{money(entryDetail.incident_unpaid_deduction)}</strong> (sobre la quincena base de {money(entryDetail.quincena_base)}).
                          </p>
                        )}
                      </>
                    ) : (
                      <Row
                        label={
                          <span data-testid="ordinary-rate-label">
                            Horas ordinarias{" "}
                            <span className="text-zinc-400">({entryDetail.ordinary_hours} h × {money(entryDetail.hourly_rate)}/h)</span>
                          </span>
                        }
                        value={money(entryDetail.ordinary_pay)}
                      />
                    )}
                    <Row
                      label={
                        <span data-testid="overtime-rate-label">
                          Horas extra{" "}
                          <span className="text-zinc-400">({entryDetail.overtime_hours} h × {money(entryDetail.overtime_hourly_rate ?? (entryDetail.hourly_rate || 0) * 1.5)}/h)</span>
                        </span>
                      }
                      value={money(entryDetail.overtime_pay)}
                    />
                    <p className="text-[10px] text-zinc-500 py-1 leading-relaxed" data-testid="extra-calc-note">
                      <strong className="text-zinc-600">Cómo se calcula la hora extra:</strong> se deriva del horario contratado y se normaliza a la quincena — no se cuenta por semanas partidas del calendario.
                      <span className="block mt-0.5">
                        Horario semanal <strong className="text-zinc-600">{entryDetail.weekly_scheduled_hours} h</strong> − tope 48 h = {(Math.max((entryDetail.weekly_scheduled_hours || 0) - 48, 0)).toFixed(2)} h/sem × 4,33 ÷ 2 = base <strong className="text-zinc-600">{entryDetail.extra_base_hours} h</strong>
                        {(entryDetail.extra_uncovered_hours || 0) > 0.001 && <> {" − "}<strong className="text-zinc-600">{Number(entryDetail.extra_uncovered_hours).toFixed(2)} h</strong> (extra programada no trabajada)</>}
                        {(entryDetail.extra_uncovered_hours || 0) < -0.001 && <> {" + "}<strong className="text-emerald-700">{Math.abs(Number(entryDetail.extra_uncovered_hours)).toFixed(2)} h</strong> (extra adicional trabajada)</>}
                        {" = "}<strong className="text-emerald-700">{entryDetail.overtime_hours} h a pagar</strong>.
                      </span>
                    </p>
                    <p className="text-[10px] text-zinc-400 py-1" data-testid="hourly-rates-note">
                      Tarifa de referencia — hora ordinaria: <strong className="text-zinc-500">{money(entryDetail.hourly_rate)}</strong> · hora extra: <strong className="text-zinc-500">{money(entryDetail.overtime_hourly_rate ?? (entryDetail.hourly_rate || 0) * 1.5)}</strong>.
                    </p>
                    {entryDetail.holiday_hours > 0 && (
                      <Row label={`Feriado (${entryDetail.holiday_hours} h)`} value={money(entryDetail.holiday_pay)} />
                    )}
                    {entryDetail.incident_pay > 0 && (
                      <Row label={`Incidencias pagadas (${entryDetail.incident_paid_pct_days} d equiv.)`} value={money(entryDetail.incident_pay)} />
                    )}
                    <div className="flex items-center justify-between pt-1.5 font-bold">
                      <span className="text-sm">Salario bruto</span>
                      <span className="text-sm font-mono">{money(entryDetail.gross_pay)}</span>
                    </div>
                  </div>
                </div>

                {/* INCIDENCIAS — desglose */}
                {(entryDetail.incident_pay_breakdown || []).length > 0 && (
                  <div className="mb-3">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 border-b border-zinc-200 pb-1 mb-1">
                      Incidencias del periodo
                    </p>
                    <div className="divide-y divide-zinc-100" data-testid="receipt-incident-breakdown">
                      {entryDetail.incident_pay_breakdown.map((ib, i) => (
                        <div key={i} className="py-1.5 text-[11px]">
                          <div className="font-medium text-zinc-800">
                            {(ib.type || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}
                            <span className="text-zinc-400"> · {ib.days_business} día(s) hábil(es)</span>
                          </div>
                          <div className="pl-3 text-zinc-600">
                            {ib.days_at_first > 0 && (
                              <div>· {ib.days_at_first} día(s) al {ib.first_pct}%</div>
                            )}
                            {ib.days_at_remainder > 0 && (
                              <div>· {ib.days_at_remainder} día(s) al {ib.remainder_pct}%</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* DEDUCCIONES */}
                <div className="mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-red-700 border-b border-zinc-200 pb-1 mb-1">Deducciones</p>
                  <div className="divide-y divide-zinc-100">
                    <Row label={`CCSS obrero (${(entryDetail.ccss_pct ?? 0).toString().replace(".", ",")}%)`} value={`-${money(entryDetail.ccss_deduction)}`} neg />
                    {entryDetail.income_tax > 0 && (
                      <Row label="Impuesto sobre la renta" value={`-${money(entryDetail.income_tax)}`} neg />
                    )}
                    {(entryDetail.vale_deductions || []).length > 0 && (
                      <div className="py-1.5">
                        <p className="text-[11px] text-zinc-600 font-medium">Adelantos de salario (VALES) — Art. 173 CT</p>
                        {(entryDetail.vale_deductions || []).map((vd) => {
                          const saldoDespues = Math.max((vd.remaining_before || 0) - vd.amount, 0);
                          return (
                            <div key={vd.vale_id} className="pl-3 py-0.5 flex items-center justify-between text-[11px]">
                              <span className="text-zinc-500">
                                · {vd.mode === "single" ? "Pago único" : `Cuota ${(vd.installments_paid || 0) + 1}/${vd.installments}`}
                                {vd.reason ? ` — ${vd.reason}` : ""}
                                <span className="text-zinc-400"> (saldo antes: {money(vd.remaining_before)} · después: {money(saldoDespues)})</span>
                              </span>
                              <span className="font-mono text-red-600">-{money(vd.amount)}</span>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between pl-3 py-0.5 text-[11px] font-semibold border-t border-zinc-100 mt-1 pt-1">
                          <span className="text-zinc-700">Subtotal vales</span>
                          <span className="font-mono text-red-700">-{money(entryDetail.vale_total || 0)}</span>
                        </div>
                      </div>
                    )}
                    {/* Deducciones personalizadas (con nombre) */}
                    {((entryDetail.custom_deductions || []).length > 0 || p.status !== "approved") && (
                      <div className="py-1.5">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] text-zinc-600 font-medium">Otras deducciones</p>
                          {p.status !== "approved" && !dedForm.open && (
                            <button
                              onClick={() => setDedForm({ open: true, concept: "", amount: "", notes: "" })}
                              className="text-[11px] text-zinc-500 hover:text-zinc-900 underline"
                              data-testid="add-deduction-btn"
                            >
                              + Agregar deducción
                            </button>
                          )}
                        </div>
                        {(entryDetail.custom_deductions || []).map((cd) => (
                          <div key={cd.id} className="pl-3 py-0.5 flex items-center justify-between text-[11px]" data-testid={`custom-deduction-${cd.id}`}>
                            <span className="text-zinc-600">
                              · {cd.concept}
                              {cd.notes ? <span className="text-zinc-400"> — {cd.notes}</span> : null}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="font-mono text-red-600">-{money(cd.amount)}</span>
                              {p.status !== "approved" && (
                                <button
                                  onClick={() => removeDeduction(cd.id)}
                                  className="text-zinc-300 hover:text-red-600"
                                  title="Eliminar deducción"
                                  data-testid={`remove-deduction-${cd.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          </div>
                        ))}
                        {dedForm.open && (
                          <div className="mt-1.5 p-2 rounded-md bg-zinc-50 border border-zinc-200 space-y-2" data-testid="deduction-form">
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Concepto (ej. Uniforme)"
                                value={dedForm.concept}
                                onChange={(e) => setDedForm({ ...dedForm, concept: e.target.value })}
                                className="h-8 text-xs"
                                data-testid="deduction-concept"
                              />
                              <Input
                                type="number"
                                placeholder="Monto"
                                value={dedForm.amount}
                                onChange={(e) => setDedForm({ ...dedForm, amount: e.target.value })}
                                className="h-8 text-xs"
                                data-testid="deduction-amount"
                              />
                            </div>
                            <Input
                              placeholder="Notas (opcional)"
                              value={dedForm.notes}
                              onChange={(e) => setDedForm({ ...dedForm, notes: e.target.value })}
                              className="h-8 text-xs"
                              data-testid="deduction-notes"
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDedForm({ open: false, concept: "", amount: "", notes: "" })} data-testid="deduction-cancel">
                                Cancelar
                              </Button>
                              <Button size="sm" className="h-7 text-xs bg-zinc-900 text-white hover:bg-zinc-800" onClick={addDeduction} disabled={savingDed} data-testid="deduction-save">
                                {savingDed ? "Guardando..." : "Agregar"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1.5 font-bold">
                      <span className="text-sm">Total deducciones</span>
                      <span className="text-sm font-mono text-red-700">
                        -{money((entryDetail.ccss_deduction || 0) + (entryDetail.income_tax || 0) + (entryDetail.other_deductions || 0))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ADICIONALES AL PAGO */}
                <div className="mb-3">
                  <div className="flex items-center justify-between border-b border-zinc-200 pb-1 mb-1">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Adicionales al pago</p>
                    {p.status !== "approved" && !addForm.open && (
                      <button
                        onClick={() => setAddForm({ open: true, concept: "", amount: "", notes: "", subject_to_ccss: false })}
                        className="text-[11px] text-zinc-500 hover:text-emerald-700 underline"
                        data-testid="add-addition-btn"
                      >
                        + Agregar adicional
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {(entryDetail.custom_additions || []).length === 0 && !addForm.open && (
                      <p className="text-[11px] text-zinc-400 py-1">Sin adicionales. Agregue bonos, viáticos, reembolsos, etc.</p>
                    )}
                    {(entryDetail.custom_additions || []).map((ad) => (
                      <div key={ad.id} className="pl-3 py-0.5 flex items-center justify-between text-[11px]" data-testid={`custom-addition-${ad.id}`}>
                        <span className="text-zinc-600">
                          · {ad.concept}
                          {ad.subject_to_ccss && (
                            <span className="ml-1 px-1 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase">CCSS</span>
                          )}
                          {ad.notes ? <span className="text-zinc-400"> — {ad.notes}</span> : null}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-emerald-700">+{money(ad.amount)}</span>
                          {p.status !== "approved" && (
                            <button
                              onClick={() => removeAddition(ad.id)}
                              className="text-zinc-300 hover:text-red-600"
                              title="Eliminar adicional"
                              data-testid={`remove-addition-${ad.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      </div>
                    ))}
                    {addForm.open && (
                      <div className="mt-1.5 p-2 rounded-md bg-emerald-50 border border-emerald-200 space-y-2" data-testid="addition-form">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="Concepto (ej. Bono)"
                            value={addForm.concept}
                            onChange={(e) => setAddForm({ ...addForm, concept: e.target.value })}
                            className="h-8 text-xs"
                            data-testid="addition-concept"
                          />
                          <Input
                            type="number"
                            placeholder="Monto"
                            value={addForm.amount}
                            onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })}
                            className="h-8 text-xs"
                            data-testid="addition-amount"
                          />
                        </div>
                        <Input
                          placeholder="Notas (opcional)"
                          value={addForm.notes}
                          onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                          className="h-8 text-xs"
                          data-testid="addition-notes"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setAddForm({ ...addForm, subject_to_ccss: true })}
                            className={`h-8 rounded-md border text-[11px] font-medium transition-colors ${addForm.subject_to_ccss ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-zinc-600 border-zinc-300 hover:border-emerald-400"}`}
                            data-testid="addition-type-salary"
                          >
                            Día de salario (con CCSS)
                          </button>
                          <button
                            type="button"
                            onClick={() => setAddForm({ ...addForm, subject_to_ccss: false })}
                            className={`h-8 rounded-md border text-[11px] font-medium transition-colors ${!addForm.subject_to_ccss ? "bg-zinc-800 text-white border-zinc-800" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}
                            data-testid="addition-type-other"
                          >
                            Otro rubro (sin CCSS)
                          </button>
                        </div>
                        <p className="text-[10px] text-zinc-500">
                          {addForm.subject_to_ccss
                            ? `Se le aplicará el rebajo de CCSS (${(entryDetail?.ccss_pct ?? 0).toString().replace(".", ",")}%) como salario.`
                            : "No se le aplica CCSS (bono, viático, reembolso, etc.)."}
                        </p>
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddForm({ open: false, concept: "", amount: "", notes: "", subject_to_ccss: false })} data-testid="addition-cancel">
                            Cancelar
                          </Button>
                          <Button size="sm" className="h-7 text-xs bg-emerald-700 text-white hover:bg-emerald-800" onClick={addAddition} disabled={savingAdd} data-testid="addition-save">
                            {savingAdd ? "Guardando..." : "Agregar"}
                          </Button>
                        </div>
                      </div>
                    )}
                    {(entryDetail.custom_additions_total || 0) > 0 && (
                      <div className="flex items-center justify-between pt-1.5 font-bold">
                        <span className="text-sm">Total adicionales</span>
                        <span className="text-sm font-mono text-emerald-700" data-testid="additions-total">+{money(entryDetail.custom_additions_total)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* NETO */}
                <div className="border-t-4 border-zinc-900 pt-3 flex items-center justify-between mb-4">
                  <span className="text-base font-bold uppercase">Neto a pagar</span>
                  <span className="text-2xl font-bold font-mono">{money(entryDetail.net_pay)}</span>
                </div>

                {/* Declaración legal */}
                <div className="border border-zinc-300 rounded p-3 text-[11px] leading-relaxed text-justify mb-3 bg-zinc-50">
                  <p className="font-bold mb-1">CONSTANCIA DE CONFORMIDAD SALARIAL</p>
                  <p>
                    Yo, <strong>{entryDetail.employee_name}</strong>, declaro haber recibido de{" "}
                    <strong>{entryDetail.company_legal_name || entryDetail.company_trade_name || defaultCompany?.legal_name || defaultCompany?.trade_name || "la empresa"}</strong>{" "}
                    la suma neta de <strong>{money(entryDetail.net_pay)}</strong> correspondiente al período
                    del <strong>{formatDate(p.start_date)}</strong> al <strong>{formatDate(p.end_date)}</strong>, según el desglose de salario
                    ordinario, horas extraordinarias, recargos, deducciones y aportes aquí consignado.
                  </p>
                  <p className="mt-1">
                    Manifiesto plena conformidad con los rubros y deducciones, y hago constar que a la fecha
                    de la presente firma <strong>no se me adeudan salarios, horas extraordinarias, recargos,
                    bonificaciones, feriados, ni ninguna otra suma de naturaleza
                    laboral</strong> del presente ni anteriores períodos que no haya sido pagada o reconocida
                    en este u otros comprobantes. Emitida de conformidad con los artículos 166-177 del
                    Código de Trabajo de Costa Rica.
                  </p>
                </div>

                {/* Firma */}
                <div className="grid grid-cols-2 gap-6 mt-5">
                  <div className="text-center">
                    {entryDetail.signature ? (
                      <>
                        <SignatureImage src={entryDetail.signature} height={70} />
                        <p className="text-[10px] text-zinc-600 mt-1">
                          <strong>{entryDetail.signature_name || entryDetail.employee_name}</strong>
                        </p>
                        {entryDetail.signature_id && <p className="text-[9px] text-zinc-500">Céd: {entryDetail.signature_id}</p>}
                        <p className="text-[9px] text-zinc-400">
                          Firmado {formatDate(entryDetail.signed_at)}
                        </p>
                      </>
                    ) : p.status === "approved" ? (
                      <>
                        <SignaturePad
                          value={pendingSignature}
                          onChange={setPendingSignature}
                          height={100}
                          label=""
                          placeholder="Firme aquí"
                          testid="payroll-signature-pad"
                        />
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <Input
                            placeholder="Cédula"
                            value={pendingSignerId}
                            onChange={(e) => setPendingSignerId(e.target.value)}
                            className="h-7 text-[11px]"
                            data-testid="payroll-signer-id"
                          />
                          <Button
                            size="sm"
                            disabled={!pendingSignature || signing}
                            onClick={async () => {
                              setSigning(true);
                              try {
                                const { data } = await api.post(
                                  `/payroll/periods/${p.id}/entries/${entryDetail.id}/sign`,
                                  {
                                    signature: pendingSignature,
                                    signature_name: entryDetail.employee_name,
                                    signature_id: pendingSignerId,
                                  }
                                );
                                setEntryDetail(data);
                                setEntries((prev) => prev.map((x) => x.id === data.id ? data : x));
                                setPendingSignature(null);
                                setPendingSignerId("");
                                toast.success("Recibo firmado");
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Error al firmar");
                              } finally {
                                setSigning(false);
                              }
                            }}
                            className="h-7 bg-zinc-900 text-white hover:bg-zinc-800 text-[11px]"
                            data-testid="payroll-sign-submit"
                          >
                            Firmar recibo
                          </Button>
                        </div>
                        <p className="text-[9px] text-zinc-400 mt-1">Firma del trabajador</p>
                      </>
                    ) : (
                      <div className="h-[100px] flex items-center justify-center border border-dashed border-zinc-200 rounded">
                        <p className="text-[10px] text-zinc-400 px-3 text-center">
                          La firma del trabajador estará disponible cuando el periodo esté <strong>aprobado</strong>.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="h-[70px] border-b border-zinc-300" />
                    <p className="text-[10px] text-zinc-600 mt-1"><strong>{p.approved_by || "RRHH"}</strong></p>
                    <p className="text-[9px] text-zinc-400">Autorizado por el patrono</p>
                  </div>
                </div>

                <p className="text-[9px] text-zinc-400 text-center mt-4 italic">
                  Tasa por hora: {money(entryDetail.hourly_rate)} · Tasa diaria: {money(entryDetail.daily_rate)} · VETCOMER Workforce System
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEntryDetail(null)} data-testid="payroll-receipt-close">
                Cerrar
              </Button>
              <Button
                onClick={() => printElement(payslipRef.current, `Comprobante ${entryDetail?.employee_code}`)}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="payroll-receipt-print"
              >
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Nodo oculto: formato formal de impresión (una hoja carta) */}
        <div style={{ position: "fixed", left: "-10000px", top: 0, width: "816px" }} aria-hidden="true">
          <PayslipPrint ref={payslipRef} entry={entryDetail} period={selectedPeriod} defaultCompany={defaultCompany} />
        </div>

        {/* Day Edit Dialog (editar registro/incidencia de un día antes de aprobar) */}
        <Dialog open={!!dayEdit} onOpenChange={(o) => !o && closeDayEdit()}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-zinc-950">
                Editar día — {dayEdit ? formatDate(dayEdit.date) : ""}
              </DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                {entryDetail?.employee_name} · {dayEdit?.day_name}
              </DialogDescription>
            </DialogHeader>
            {dayEdit && (
              <div className="space-y-4 py-2">
                {dayEdit.is_holiday && (
                  <div
                    className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/70"
                    data-testid="holiday-single-box"
                  >
                    <div className="pr-1">
                      <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">Feriado</p>
                      <p className="text-[11px] text-amber-800 leading-snug mt-0.5">
                        Por ley, un feriado trabajado se paga <strong>doble</strong>. Actívalo si este día se
                        trabajó como <strong>sencillo</strong> (se quita el recargo).
                      </p>
                    </div>
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <Switch
                        checked={!!dayEdit.holiday_single}
                        onCheckedChange={(v) => toggleHolidaySingle(v)}
                        disabled={savingDay}
                        data-testid="holiday-single-switch"
                      />
                      <span className="text-[10px] font-semibold text-amber-800" data-testid="holiday-single-state">
                        {dayEdit.holiday_single ? "Sencillo" : "Doble"}
                      </span>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Tipo de registro</Label>
                  <Select
                    value={dayForm.mode}
                    onValueChange={(v) => setDayForm({ ...dayForm, mode: v })}
                  >
                    <SelectTrigger data-testid="day-edit-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attendance">Trabajado (entrada/salida)</SelectItem>
                      <SelectItem value="incident">Incidencia (incapacidad, vacaciones, permiso, ausencia)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {dayForm.mode === "attendance" ? (
                  <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-zinc-700">Entrada</Label>
                      <Input
                        type="time"
                        value={dayForm.check_in_time || ""}
                        onChange={(e) => setDayForm({ ...dayForm, check_in_time: e.target.value })}
                        data-testid="day-edit-check-in"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-700">Salida</Label>
                      <Input
                        type="time"
                        value={dayForm.check_out_time || ""}
                        onChange={(e) => setDayForm({ ...dayForm, check_out_time: e.target.value })}
                        data-testid="day-edit-check-out"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-zinc-700">Salida a permiso/cita (opcional)</Label>
                      <Input
                        type="time"
                        value={dayForm.unpaid_break_out_time || ""}
                        onChange={(e) => setDayForm({ ...dayForm, unpaid_break_out_time: e.target.value })}
                        data-testid="day-edit-break-out"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-700">Regreso del permiso/cita (opcional)</Label>
                      <Input
                        type="time"
                        value={dayForm.unpaid_break_in_time || ""}
                        onChange={(e) => setDayForm({ ...dayForm, unpaid_break_in_time: e.target.value })}
                        data-testid="day-edit-break-in"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500">
                    Si el trabajador salió a una cita/permiso y regresó, registre esas dos marcas: el tiempo entre ellas <strong>NO se paga</strong>.
                  </p>
                  <label className="flex items-start gap-2 mt-1 p-2 rounded-md bg-amber-50 border border-amber-200 cursor-pointer" data-testid="authorize-extra-label">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!dayForm.authorize_outside_as_extra}
                      onChange={(e) => setDayForm({ ...dayForm, authorize_outside_as_extra: e.target.checked })}
                      data-testid="authorize-extra-checkbox"
                    />
                    <span className="text-[11px] text-amber-900 leading-snug">
                      <strong>Autorizar tiempo fuera de horario como EXTRA</strong> — cuenta como extra todo lo trabajado fuera de la jornada del contrato (entrada antes o salida después). Se respeta la ordinaria pactada; el resto va a extra.
                    </span>
                  </label>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs font-medium text-zinc-700">Tipo de incidencia</Label>
                      <Select
                        value={dayForm.incident_type}
                        onValueChange={(v) => setDayForm({ ...dayForm, incident_type: v })}
                      >
                        <SelectTrigger data-testid="day-edit-incident-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INCIDENT_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {(dayForm.incident_type === "incapacidad_ccss" || dayForm.incident_type === "incapacidad_ins") && (
                      <div>
                        <Label className="text-xs font-medium text-zinc-700">Folio (CCSS/INS)</Label>
                        <Input
                          placeholder="Folio del comprobante (opcional)"
                          value={dayForm.folio || ""}
                          onChange={(e) => setDayForm({ ...dayForm, folio: e.target.value })}
                          data-testid="day-edit-folio"
                        />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs font-medium text-zinc-700">Notas</Label>
                      <Input
                        placeholder="Motivo / observación (opcional)"
                        value={dayForm.notes || ""}
                        onChange={(e) => setDayForm({ ...dayForm, notes: e.target.value })}
                        data-testid="day-edit-notes"
                      />
                    </div>
                  </>
                )}

                <p className="text-[11px] text-zinc-500">
                  Al guardar, la nómina de este periodo se <strong>recalcula</strong> automáticamente.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeDayEdit} data-testid="day-edit-cancel">
                Cancelar
              </Button>
              <Button
                onClick={saveDayEdit}
                disabled={savingDay}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="day-edit-save"
              >
                {savingDay ? "Guardando..." : "Guardar y recalcular"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────
  // LIST VIEW
  // ──────────────────────────────────────────────────────────────────────
  return (
    <div data-testid="payroll-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Nómina
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Periodos de pago quincenales (Costa Rica)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PayrollSimulator />
          <Link
            to="/nomina/reconciliacion"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-zinc-200 text-sm text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
            data-testid="payroll-reconciliation-link"
            title="Pendientes de revisión y ajustes pendientes"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Reconciliación</span>
          </Link>
          <Button
            onClick={() => { setForm(emptyForm); setDialogOpen(true); }}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="add-period-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo periodo
          </Button>
        </div>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Periodo</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Rango</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">Sucursal</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Empleados</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden md:table-cell">Bruto</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Neto</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Estado</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {periods.map((p) => (
                <TableRow
                  key={p.id}
                  className="border-zinc-100 cursor-pointer hover:bg-zinc-50/80 transition-colors"
                  onClick={() => openDetail(p.id)}
                  data-testid={`period-row-${p.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                      <div>
                        <p className="text-sm text-zinc-900 font-medium">
                          {MONTHS[p.month]} {p.year}
                        </p>
                        <p className="text-[11px] text-zinc-400">Quincena {p.half}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">{formatDate(p.start_date)} → {formatDate(p.end_date)}</TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden md:table-cell">{branchName(p.branch_id)}</TableCell>
                  <TableCell className="text-right text-sm text-zinc-900 font-mono">{p.employee_count}</TableCell>
                  <TableCell className="text-right text-sm text-zinc-700 font-mono hidden md:table-cell">{money(p.total_gross)}</TableCell>
                  <TableCell className="text-right text-sm text-zinc-950 font-semibold font-mono">{money(p.total_net)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`font-medium ${statusMap[p.status]?.cls || ""}`}>
                      {statusMap[p.status]?.label || p.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); handleDelete(p.id, p.status); }}
                        data-testid={`delete-period-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {periods.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16 text-zinc-400">
                    <Receipt className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay periodos de nómina creados</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">Nuevo periodo de nómina</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Los periodos quincenales corren del 1-15 y 16 al fin de mes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-zinc-700">Año</Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  data-testid="period-year-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Mes</Label>
                <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: v })}>
                  <SelectTrigger data-testid="period-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.slice(1).map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Quincena</Label>
                <Select value={String(form.half)} onValueChange={(v) => setForm({ ...form, half: v })}>
                  <SelectTrigger data-testid="period-half-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 (1-15)</SelectItem>
                    <SelectItem value="2">2 (16-fin)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Sucursal (opcional)</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="period-branch-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="period-cancel-button">
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="period-submit-button"
            >
              Crear periodo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold, neg }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-xs ${bold ? "font-bold text-zinc-900" : "text-zinc-600"}`}>{label}</span>
      <span className={`text-xs font-mono ${bold ? "font-bold text-zinc-900" : neg ? "text-red-600" : "text-zinc-800"}`}>
        {value}
      </span>
    </div>
  );
}
